import { DataProvider, HttpError } from 'react-admin';

import { requireAuth, requirePartner } from '@src/providers/dataProvider/shared';

import { fetchLookerDashboards } from './fetchLookerDashboards';
import type { ILookerDashboard } from './types';

type LookerDashboardRecord = ILookerDashboard & { id: string };

/**
 * Internal interface with proper types for the looker-dashboards data provider.
 * React-admin's DataProvider is generic which prevents type-safe single-resource providers.
 */
interface LookerDashboardsDataProviderImpl {
  getList: () => Promise<{ data: LookerDashboardRecord[], total: number }>;
  getOne: () => Promise<never>;
  getMany: () => Promise<never>;
  getManyReference: () => Promise<never>;
  create: () => Promise<never>;
  update: () => Promise<never>;
  updateMany: () => Promise<never>;
  delete: () => Promise<never>;
  deleteMany: () => Promise<never>;
}

/**
 * Data provider for the looker-dashboards resource. Read-only: dashboards are
 * defined in Looker itself, this only surfaces the list available to the
 * current partner (resolved on the backend from the partner's
 * lookerServiceConfig setting).
 */
const lookerDashboardsDataProviderImpl: LookerDashboardsDataProviderImpl = {
  getList: async () => {
    try {
      const token = requireAuth();
      const partner = requirePartner();

      const dashboards = await fetchLookerDashboards({ partnerCode: partner.code, token });

      const data: LookerDashboardRecord[] = dashboards.map(dashboard => ({
        ...dashboard,
        id: dashboard.id,
      }));

      return { data, total: data.length };
    } catch (error) {
      console.error('lookerDashboardsDataProvider.getList failed:', {
        error: error instanceof Error ? error.message : error,
      });
      if (error instanceof HttpError) throw error;
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to load Looker dashboards.', status);
    }
  },

  // Not implemented - looker-dashboards is read-only
  getOne: async () => { throw new Error('getOne not implemented for looker-dashboards'); },
  getMany: async () => { throw new Error('getMany not implemented for looker-dashboards'); },
  getManyReference: async () => { throw new Error('getManyReference not implemented for looker-dashboards'); },
  create: async () => { throw new Error('create not implemented for looker-dashboards'); },
  update: async () => { throw new Error('update not implemented for looker-dashboards'); },
  updateMany: async () => { throw new Error('updateMany not implemented for looker-dashboards'); },
  delete: async () => { throw new Error('delete not implemented for looker-dashboards'); },
  deleteMany: async () => { throw new Error('deleteMany not implemented for looker-dashboards'); },
};

// Cast to DataProvider for react-admin compatibility.
// React-admin's DataProvider methods are generic, which prevents type-safe
// single-resource providers. We define proper types above and cast here.
export const lookerDashboardsDataProvider = lookerDashboardsDataProviderImpl as unknown as DataProvider;

export default lookerDashboardsDataProvider;
